import {
    array,
    boolean,
    enum as zodEnum,
    nativeEnum,
    object,
    string,
    TypeOf,
} from 'zod';

import { Alignment } from './common';
import { LabelDisplay } from './label';
import {
    BannerOptions,
    CarouselOptions,
    GridOptions,
    SwimlaneOptions,
} from './page';
import { TileOptions } from './page/tile';
import { GroupDisplay, SubscriptionPageDisplay } from './subscription';

/* ---------------------------------- Enums --------------------------------- */

export enum RegistrationMode {
    InviteOnly = 'invite-only',
    Required = 'required',
    RequiredForPurchase = 'required-for-purchase',
    OptionalForDevices = 'optional-for-devices',
    Optional = 'optional',
    Disabled = 'disabled',
}

export enum VerificationPrompt {
    PreRegistration = 'pre-registration',
    None = 'none',
}

export enum RegistrationPrompt {
    PreVerification = 'pre-verification',
    PostVerification = 'post-verification',
    None = 'none',
}

export enum PairingRedirectDefault {
    Login = 'login',
    Registration = 'registration',
}

/* -------------------------------- CacheMode ------------------------------- */

export const CacheMode = zodEnum(['on', 'off', 'session']);
export type CacheMode = TypeOf<typeof CacheMode>;

/* -------------------------------- Analytics ------------------------------- */

export const AnalyticsConfig = object({
    mux: object({
        envKey: string().optional(),
    }).optional(),
    google: object({
        measurementId: string().optional(),
    }).optional(),
    brightcove: object({
        enabled: boolean().optional(),
        accountId: string().optional(),
    }).optional(),
});
export type AnalyticsConfig = TypeOf<typeof AnalyticsConfig>;

/* ---------------------------- RegistrationField --------------------------- */

export const RegistrationField = object({
    required: boolean(),
    prompt: nativeEnum(RegistrationPrompt),
});
export type RegistrationField = TypeOf<typeof RegistrationField>;

/* ------------------------------ CachedEntity ------------------------------ */

export const CachedEntity = object({
    expire: string().transform(() => 'P0DT0H1M0.000S'),
    update: string().transform(() => 'P0DT0H1M0.000S'),
});
export type CachedEntity = TypeOf<typeof CachedEntity>;

/* --------------------------------- Config --------------------------------- */

export const Config = object({
    name: string(),
    analytics: AnalyticsConfig,
    cache: object({
        mode: CacheMode,
        appReload: string().optional(),
        ageRating: CachedEntity,
        banner: CachedEntity,
        category: CachedEntity,
        categoryType: CachedEntity,
        collection: CachedEntity,
        config: CachedEntity,
        dictionary: CachedEntity,
        footer: CachedEntity,
        label: CachedEntity,
        menu: CachedEntity,
        page: CachedEntity,
        person: CachedEntity,
        personType: CachedEntity,
        product: CachedEntity,
        theme: CachedEntity,
    }),
    footerId: string().optional(),
    label: object({
        tile: LabelDisplay,
        item: LabelDisplay,
    }),
    language: object({
        default: string().optional(),
    }),
    page: object({
        straightToFeature: boolean(),
        collection: object({
            alignment: nativeEnum(Alignment),
            tile: TileOptions,
        }),
        person: object({
            alignment: nativeEnum(Alignment),
            tile: TileOptions,
        }),
        custom: object({
            flex: object({
                name: object({
                    alignment: nativeEnum(Alignment),
                    visible: boolean(),
                }),
                description: object({
                    alignment: nativeEnum(Alignment),
                    visible: boolean(),
                }),
                elements: object({
                    carousel: CarouselOptions,
                    banner: BannerOptions,
                    grid: GridOptions,
                    swimlane: SwimlaneOptions,
                    videoCarousel: CarouselOptions,
                }),
            }),
            info: object({
                name: object({
                    alignment: nativeEnum(Alignment),
                    visible: boolean(),
                }),
                description: object({
                    alignment: nativeEnum(Alignment),
                    visible: boolean(),
                }),
            }),
        }),
        product: object({
            alignment: nativeEnum(Alignment),
            autoplayTrailer: boolean(),
            categoryTypes: array(string()),
            personTypes: array(string()),
            favourite: boolean(),
        }),
        search: object({
            suggestedCollection: string().optional(),
            tile: TileOptions,
            enabled: boolean(),
        }),
    }),
    purchase: object({
        code: object({
            url: string(),
            qrCodeUrl: string(),
        }).optional(),
    }),
    registration: object({
        default: nativeEnum(PairingRedirectDefault),
        mode: nativeEnum(RegistrationMode),
        email: object({
            verificationPrompt: nativeEnum(VerificationPrompt),
        }),
        fields: object({
            password: RegistrationField,
            firstName: RegistrationField,
            lastName: RegistrationField,
            postCode: RegistrationField,
            gender: RegistrationField,
            yearOfBirth: RegistrationField,
            marketingOptIn: RegistrationField,
        }),
    }),
    stripe: object({
        accountId: string(),
        publishableKey: string(),
    }),
    subscription: object({
        subscriptionPage: SubscriptionPageDisplay,
        group: GroupDisplay,
    }),
    video: object({
        drm: object({
            fairplay: object({
                certificate: string().optional(),
            }),
        }),
    }),
    // Nothing in here is relevant to the CW
    // AppleTV only - left as <any>
    device: object({
        web: object({
            enabled: boolean(),
            siteType: zodEnum(['site', 'microsite']),
        }).passthrough(),
    }).passthrough(),
});

export type Config = TypeOf<typeof Config>;
